import { Button, TextInput, Spacing, FormField } from 'zens-storybook'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { StyleSheet, View } from 'react-native'
import { object, string } from 'yup'
import { useStore, ProfileStatus, useProfile } from 'zens-store'
import { useRouter, useNumberParams, useParams, Redirect } from 'zens-router'
import { AuthPanel } from '@dms/component'
import { ValidationError } from '@dms/helper'

const styles = StyleSheet.create({
  input: {
    marginBottom: Spacing.XS,
    minHeight: 92,
  },
  email: {
    marginHorizontal: Spacing.XL,
    marginBottom: Spacing.L,
  },
  button: {
    minWidth: 100,
  },
})

const AuthName = () => {
  const { t } = useTranslation()
  const [{ companyId }] = useNumberParams()
  const [{ name: nameParam, token }] = useParams()
  const [name, setName] = useState(nameParam || '')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const router = useRouter()
  const { auth, message } = useStore()
  const profile = useProfile({ token })

  const schema = object({
    email: string().required(t('Email address is required')),
    name: string().required(t('Name is required')),
  })

  useEffect(() => {
    if (profile.data?.id && auth.profile?.id === profile.data?.id) {
      console.debug('Already logged in correctly')
      if (companyId) router.push(`/companies/${companyId}`)
      else router.push('/')
    } else if (profile.data?.status === ProfileStatus.Active) {
      const email = profile.data?.email
      router.push({ pathname: `/signin`, params: { email, companyId } })
    }
  }, [profile.data])

  useEffect(() => {
    if (profile.isError) {
      const { message: errorMessage } = profile.error as Error
      console.debug(`AUTHNAME: Profile load failed: ${errorMessage}`)
      message.add({
        message: `${t('Profile data could not be loaded')}. ${errorMessage}`,
        status: 'error',
      })
    }
  }, [profile.isError])

  useEffect(() => {
    setErrors({})
  }, [name])

  const goNext = async () => {
    try {
      schema.validateSync({ email: profile.data?.email, name })
    } catch (error) {
      const { message, path } = error as ValidationError
      setErrors({ [path]: message })
      return
    }

    router.push({ pathname: `/signup`, params: { token, name, companyId } })
  }

  if (!profile.isFetched) return <View />

  if (profile.data?.email === undefined) {
    return <Redirect href={{ pathname: '/forbidden' }} />
  }

  return (
    <AuthPanel
      title={t('Sign up')}
      bottom={<Button title={t('Next')} style={styles.button} onPress={goNext} testID="AuthName.Submit" />}
    >
      <FormField disabled error={errors.email} style={styles.email}>
        <TextInput value={profile.data?.email} disabled error={!!errors.email} round />
      </FormField>
      <FormField label={t('Full name')} error={errors.name} style={styles.input}>
        <TextInput
          placeholder={t('Enter your name here')}
          value={name}
          onChangeText={setName}
          onSubmitEditing={goNext}
          error={!!errors.name}
          autoFocus
          testID="AuthName.Name"
        />
      </FormField>
    </AuthPanel>
  )
}

export default AuthName
